import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, qaPage, speakable, aeoGeo } from "@/lib/seo/schema";

const PATH = "/ord-per-minut";
const ANSWER =
  "På svenska säger man normalt 130–150 ord per minut i lugnt berättartempo. En film på 60 sekunder rymmer därför cirka 140 ord speakertext, en film på 30 sekunder cirka 70 ord och en på 15 sekunder cirka 35 ord — mindre om bilden eller grafiken behöver luft.";

const ROWS: [string, string, string][] = [
  ["6 sek", "13–15 ord", "Bumper / annons"],
  ["15 sek", "33–38 ord", "Kort annons, Reels, TikTok"],
  ["30 sek", "65–75 ord", "Reklamfilm, LinkedIn"],
  ["60 sek", "130–150 ord", "Företagsfilm, explainer"],
  ["90 sek", "195–225 ord", "Rekryteringsfilm, kundcase"],
  ["2 min", "260–300 ord", "Produktgenomgång"],
  ["3 min", "390–450 ord", "Utbildning, instruktion"],
  ["5 min", "650–750 ord", "Föreläsning, intern film"],
];

const FAQ: LandingFAQ[] = [
  { q: "Hur många ord säger man per minut?", a: "I lugnt svenskt berättartempo 130–150 ord per minut. I ett vanligt samtal går det fortare, ofta 150–170 ord per minut, men speakertext till film ska ha marginal." },
  { q: "Hur långt manus behöver en film på en minut?", a: "Cirka 140 ord. Planera hellre för 120 ord om filmen har grafik, produktbilder eller pauser där bilden ska tala." },
  { q: "Varför blir filmen längre än manuset?", a: "Pauser, betoningar och bildövergångar tar tid. Läs manuset högt med tidtagarur innan inspelning — det är det säkraste testet." },
  { q: "Räknas siffror och förkortningar som ord?", a: "Räkna dem som de uttalas. \"2026\" blir tre till fyra talade ord, och förkortningar som läses ut bokstav för bokstav tar längre tid än ett vanligt ord." },
];

const TITLE = "Hur många ord säger man per minut? Tabell för manus";
const DESC =
  "Svenska talas i 130–150 ord per minut. Tabell från 6 sekunder till 5 minuter som visar hur långt manus en film behöver — med tumregler för speakertext.";

export const Route = createFileRoute("/ord-per-minut")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: PATH, name: TITLE, description: DESC })) },
      { type: "application/ld+json", children: JSON.stringify(qaPage({ question: "Hur många ord säger man per minut?", answer: ANSWER, path: PATH, authorName: "Fenny" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Manus och koncept", path: "/manus-och-koncept" }, { name: "Ord per minut", path: PATH }])) },
    ],
  }),
  component: Page,
});

const a = "underline decoration-mint/40 underline-offset-4 hover:text-mint";

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Svar — manus"
        title={<>Hur många ord säger man per minut?</>}
        lede={ANSWER}
        tldr={<p>Räkna <strong>130–150 ord per minut</strong>. 60 sekunder ≈ <strong>140 ord</strong>, 30 sekunder ≈ <strong>70 ord</strong>, 15 sekunder ≈ <strong>35 ord</strong>.</p>}
        ctaPrimary={{ label: "Få hjälp med manus", to: "/manus-och-koncept" }}
        ctaSecondary={{ label: "Boka strategimöte", to: "/kontakt" }}
        trackingId="ord-per-minut"
        sections={[
          {
            id: "tabell",
            heading: "Tabell: filmlängd och antal ord",
            body: (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-left text-sm">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr><th className="py-2 pr-4 font-medium">Filmlängd</th><th className="py-2 pr-4 font-medium">Antal ord</th><th className="py-2 font-medium">Typisk användning</th></tr>
                  </thead>
                  <tbody>
                    {ROWS.map(([l, w, u]) => (
                      <tr key={l} className="border-b border-border/60"><td className="py-2 pr-4">{l}</td><td className="py-2 pr-4 font-semibold">{w}</td><td className="py-2 text-muted-foreground">{u}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            id: "tumregler",
            heading: "Tre tumregler för speakertext",
            body: (
              <p>
                <strong>Skriv kortare än du tror.</strong> Sikta på den lägre siffran i tabellen.{" "}
                <strong>Läs högt med klocka.</strong> Ett manus som läses tyst blir alltid för långt.{" "}
                <strong>En tanke per mening.</strong> Talat språk tål inte bisatser. Läs mer om hur ett{" "}
                <a className={a} href="/manus-och-koncept">manus för film</a> byggs upp, hur det översätts till{" "}
                <a className={a} href="/storyboard">storyboard</a> och vad en{" "}
                <a className={a} href="/voice-over">speakerröst</a> kostar.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        closing={{
          title: "Vill ni ha ett manus som håller tiden?",
          body: "Vi skriver manus som läses in och tidsätts innan inspelning.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "process"]} />
    </>
  );
}
